/**
 * Button — the single primary-CTA control of the design system.
 *
 * Variants: `primary` (the one main CTA per screen — ocre fill), `secondary`
 * (warm `primarySubtle` fill, for support controls), `ghost`.
 * Sizes: `md` (default web), `lg` and `xl` (glove-friendly planta targets —
 * 56px / 64px tall). References semantic tokens only via {@link useTheme}.
 */

import React from 'react';
import { useTheme } from '../theme/index.js';
import type { Theme } from '../tokens/index.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'md' | 'lg' | 'xl';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Stretch to the full width of the container (common for planta CTAs). */
  readonly fullWidth?: boolean;
  /** Optional leading element (icon). */
  readonly leadingIcon?: React.ReactNode;
  readonly style?: React.CSSProperties;
  readonly children?: React.ReactNode;
}

interface VariantColors {
  readonly bg: string;
  readonly bgHover: string;
  readonly bgActive: string;
  readonly fg: string;
  readonly border: string;
}

function variantColors(theme: Theme, variant: ButtonVariant): VariantColors {
  const { color } = theme;
  switch (variant) {
    case 'primary':
      return {
        bg: color.primary,
        bgHover: color.primaryHover,
        bgActive: color.primaryActive,
        fg: color.onPrimary,
        border: 'transparent',
      };
    case 'secondary':
      return {
        bg: color.primarySubtle,
        bgHover: color.selected,
        bgActive: color.selected,
        fg: color.textPrimary,
        border: 'transparent',
      };
    case 'ghost':
      return {
        bg: 'transparent',
        bgHover: color.hover,
        bgActive: color.selected,
        fg: color.accent,
        border: 'transparent',
      };
    default: {
      const exhaustive: never = variant;
      return exhaustive;
    }
  }
}

function sizeMetrics(
  theme: Theme,
  size: ButtonSize,
): { height: number; fontSize: number; paddingX: number } {
  const { size: sz, space } = theme.spacing;
  switch (size) {
    case 'md':
      return { height: sz.ctaHeight, fontSize: theme.typography.size.secondary, paddingX: space[5] };
    case 'lg':
      return {
        height: sz.touchTargetPlanta,
        fontSize: theme.typography.size.bodyLg,
        paddingX: space[7],
      };
    case 'xl':
      return {
        height: sz.ctaHeightPlanta,
        fontSize: theme.typography.size.title3,
        paddingX: space[8],
      };
    default: {
      const exhaustive: never = size;
      return exhaustive;
    }
  }
}

export function Button(props: ButtonProps): React.ReactElement {
  const {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    leadingIcon,
    style,
    children,
    disabled = false,
    onMouseEnter,
    onMouseLeave,
    onMouseDown,
    onMouseUp,
    ...rest
  } = props;

  const theme = useTheme();
  const colors = variantColors(theme, variant);
  const metrics = sizeMetrics(theme, size);
  const [hover, setHover] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const [focusVisible, setFocusVisible] = React.useState(false);

  const bg = disabled
    ? theme.color.surfaceSunken
    : active
      ? colors.bgActive
      : hover
        ? colors.bgHover
        : colors.bg;

  const composedStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.space[3],
    minWidth: metrics.height,
    height: metrics.height,
    paddingInline: metrics.paddingX,
    width: fullWidth ? '100%' : undefined,
    backgroundColor: bg,
    color: disabled ? theme.color.textDisabled : colors.fg,
    border: `1px solid ${disabled ? theme.color.border : colors.border}`,
    borderRadius: theme.radii.radius.button,
    fontFamily: theme.typography.family.sans,
    fontSize: metrics.fontSize,
    fontWeight: size === 'md' ? theme.typography.weight.medium : theme.typography.weight.semibold,
    lineHeight: theme.typography.lineHeight.tight,
    cursor: disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none',
    transition: 'background-color 150ms ease-out, box-shadow 150ms ease-out',
    outline: 'none',
    boxShadow: focusVisible ? theme.radii.shadow.focusRing : undefined,
    ...style,
  };

  return React.createElement(
    'button',
    {
      ...rest,
      type: rest.type ?? 'button',
      disabled,
      'aria-disabled': disabled,
      style: composedStyle,
      // Keyboard focus shows the solid 2px ring; a mouse click does not.
      onFocus: (e: React.FocusEvent<HTMLButtonElement>) => {
        setFocusVisible(e.currentTarget.matches(':focus-visible'));
        rest.onFocus?.(e);
      },
      onBlur: (e: React.FocusEvent<HTMLButtonElement>) => {
        setFocusVisible(false);
        rest.onBlur?.(e);
      },
      onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
        setHover(true);
        onMouseEnter?.(e);
      },
      onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
        setHover(false);
        setActive(false);
        onMouseLeave?.(e);
      },
      onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => {
        setActive(true);
        onMouseDown?.(e);
      },
      onMouseUp: (e: React.MouseEvent<HTMLButtonElement>) => {
        setActive(false);
        onMouseUp?.(e);
      },
    },
    leadingIcon,
    children,
  );
}
